import { dimensionPageSize, getEmployeesQueryData, getTimesheetQueryData, getUserQueryData, queryKeyFn } from '@sit/client-shared';
import { getCompanyFromWebSessionStorage } from '@web/helpers/session';
import { queryClient } from '@web/query-client';
import { all, put } from 'redux-saga/effects';
import { reportError } from '../../helpers/newrelic';
import { calculateDefaultRowValues } from '../../helpers/rows';
import * as AutofillActions from '../Autofill/actions';
import { findDepartmentDimension, findLocationDimension } from '../selectors';

function* HandleAutofillValues({ autofillValues, from, rowId }) {
  yield all(autofillValues.map((a) => put(AutofillActions.setAutofill({ rowId, a, from }))));
}

// To remove when we remove the saga that uses this (GenerateAutofill)
const getCompanyData = () => {
  const companyId = getCompanyFromWebSessionStorage()?.id;
  /** @type {import('@sit/client-shared').CompanyType | undefined} */
  const company = queryClient.getQueryData(
    queryKeyFn({
      path: '/companies',
      operationId: 'getApiCompany',
      variables: {
        queryParams: {
          companyId,
        },
      },
    }),
  );
  return company;
};

/**
 *     Company
 */
function getTimesheetData(timesheetData, companyData) {
  return {
    showBillable: timesheetData.showBillable === undefined ? !!companyData?.showBillable : timesheetData.showBillable,
    showCustom: timesheetData.showCustom === undefined ? !!companyData?.showCustom : timesheetData.showCustom,
    showDepartmentLocation:
      timesheetData.showDepartmentLocation === undefined ? !!companyData?.showDepartmentLocation : timesheetData.showDepartmentLocation,
    showItem: timesheetData.showItem === undefined ? !!companyData?.showItem : timesheetData.showItem,
    showProject: timesheetData.showProject === undefined ? !!companyData?.showProject : timesheetData.showProject,
    showTask: timesheetData.showTask === undefined ? !!companyData?.showTask : timesheetData.showTask,
    showTimeType: timesheetData.showTimeType === undefined ? !!companyData?.showTimeType : timesheetData.showTimeType,
  };
}

function* GenerateRowAutofill({ row, store }) {
  try {
    const timesheet = getTimesheetQueryData(queryClient, row.timesheet_id);
    if (!timesheet) return;
    const companyData = getCompanyData();
    const timesheetData = getTimesheetData(timesheet, companyData);

    let projectId = 'all';

    if (timesheetData.showProject) {
      projectId =
        row.entries != null && row.entries.length > 0 && row.entries[0]?.confirmedProjectId ? row.entries[0].confirmedProjectId : undefined;
    }

    /** @type {[import('@tanstack/react-query').QueryKey, import('@tanstack/react-query').InfiniteData<import('@sit/client-shared').GetApiTasksResponse> | undefined][]} */
    const tasksQueriesData = queryClient.getQueriesData(
      queryKeyFn({
        path: '/tasks',
        operationId: 'getApiTasks',
        variables: {
          queryParams: {
            timesheetId: timesheet.id,
            activeOnly: 'true',
            pageNumber: '1',
          },
        },
      }),
    );

    /** @type {import('@sit/client-shared').GetApiTasksResponse> | undefined} */
    let tasksData = undefined;

    // We want to find the queryKey that includes the max pageSize, if it exists.
    if (tasksQueriesData) {
      /** @type {import('@sit/client-shared').GetApiTasksResponse[number] | undefined} */
      for (const [queryKey, queryData] of tasksQueriesData) {
        // Make sure to get the data from the page size without the infinite query
        if (queryKey[1].projectId === projectId) {
          tasksData = queryData?.pages.flat();
          break;
        }
      }
    }

    tasksData ??= tasksQueriesData?.[0]?.[1]?.pages?.flat();

    /** @type {[import('@tanstack/react-query').QueryKey, import('@sit/client-shared').GetApiDimensionsResponse | undefined][]} */
    const dimensionsQueriesData = queryClient.getQueriesData(
      queryKeyFn({
        path: '/dimensions',
        operationId: 'getApiDimensions',
        variables: {
          queryParams: {
            activeOnly: 'true',
            timesheetId: timesheet.id,
          },
        },
      }),
    );

    let dimensionsData = dimensionsQueriesData?.[0]?.[1];
    // We want to find the queryKey that includes `projectId` if it exists.
    // If it doesn't exist, we want to use the first queryKey that includes `timesheetId`.
    if (dimensionsQueriesData && projectId)
      for (const [queryKey, queryData] of dimensionsQueriesData) {
        if (queryKey.length === 2 && queryKey[1].projectId === projectId) {
          dimensionsData = queryData;
          break;
        }
      }

    const departmentOptions = findDepartmentDimension(dimensionsData)?.values || [];
    const locationOptions = findLocationDimension(dimensionsData)?.values || [];
    const userData = getUserQueryData(queryClient);
    /**
     * @type {import('@sit/client-shared').AutofillValue[] | undefined}
     */
    let userAutofillValues = [];
    if ((!timesheet.user_id && !timesheet.externalEmployeeId) || userData?.id === timesheet.user_id) {
      userAutofillValues = userData?.autofillValues;
    } else if (timesheet.externalEmployeeId) {
      const employeesData = getEmployeesQueryData(queryClient);
      let timesheetUser;
      for (const [queryKey, queryData] of employeesData) {
        // ['employees']
        if (queryKey.length === 1) {
          timesheetUser = queryData?.employees.find((u) => u.employeeId === timesheet.externalEmployeeId);
          if (timesheetUser) break;
        }
      }
      userAutofillValues = timesheetUser?.autofillValues;
    }
    const { pClientId, pDepartment, pDepartmentObj, pItemId, pLocation, pLocationObj, pProjectId, pTaskId, taskObj } =
      calculateDefaultRowValues({ row, timesheetDimensions: timesheetData, tasksData, departmentOptions, locationOptions });
    if (userAutofillValues && userAutofillValues.length > 0) {
      yield HandleAutofillValues({ autofillValues: userAutofillValues, rowId: row.id, from: 'user' });
    }
    if (pClientId) {
      /** @type {[import('@tanstack/react-query').QueryKey, import('@tanstack/react-query').InfiniteData<import('@sit/client-shared').GetApiClientsResponse> | undefined][]} */
      const clientsQueriesData = queryClient.getQueriesData(
        queryKeyFn({
          path: '/clients',
          operationId: 'getApiClients',
          variables: {},
        }),
      );
      const clientsData = clientsQueriesData?.[0]?.[1]?.pages?.flat();
      const clientObj = clientsData?.find((c) => c.id === pClientId) || row?.dimensionValues?.client;
      if (clientObj?.autofillValues && clientObj.autofillValues.length > 0) {
        yield HandleAutofillValues({ autofillValues: clientObj.autofillValues, rowId: row.id, from: pClientId });
      }
    }
    if (pProjectId) {
      /** @type {[import('@tanstack/react-query').QueryKey, import('@tanstack/react-query').InfiniteData<import('@sit/client-shared').GetApiProjectsResponse> | undefined][]} */
      const projectsQueriesData = queryClient.getQueriesData(
        queryKeyFn({
          path: '/projects',
          operationId: 'getApiProjects',
          variables: {},
        }),
      );
      const projectsData = projectsQueriesData?.[0]?.[1]?.pages?.flat();
      const projectObj = projectsData?.find((c) => c.id === pProjectId) || row?.dimensionValues?.project;
      if (projectObj?.autofillValues?.length > 0) {
        yield HandleAutofillValues({ autofillValues: projectObj.autofillValues, rowId: row.id, from: pProjectId });
      }
    }
    const taskAutofillValues = (taskObj?.autofillValues && taskObj.autofillValues.length > 0) || row?.dimensionValues?.task?.autofillValues;
    if (pTaskId && taskAutofillValues && taskAutofillValues.length > 0) {
      yield HandleAutofillValues({ autofillValues: taskAutofillValues, rowId: row.id, from: pTaskId });
    }
    if (pItemId) {
      /** @type {[import('@tanstack/react-query').QueryKey, import('@tanstack/react-query').InfiniteData<import('@sit/client-shared').GetApiItemsResponse> | undefined][]} */
      const itemsQueriesData = queryClient.getQueriesData(
        queryKeyFn({
          path: '/items',
          operationId: 'getApiItems',
          variables: {
            queryParams: {
              activeOnly: 'true',
              timesheetId: timesheet.id,
            },
          },
        }),
      );
      // We want to find the queryKey that includes the max pageSize, if it exists.
      if (itemsQueriesData) {
        /** @type {import('@sit/client-shared').GetApiItemsResponse[number] | undefined} */
        let itemObj = undefined;
        for (const [queryKey, queryData] of itemsQueriesData) {
          // Make sure to get the data from the page size without the infinite query
          if (queryKey.length === 3 && queryKey[1].pageSize === `${dimensionPageSize}`) {
            for (const page of queryData.pages) {
              itemObj = page.find((c) => c.id === pItemId);
              if (itemObj) {
                break;
              }
            }
            break;
          }
        }
        if (itemObj?.autofillValues && itemObj.autofillValues.length > 0) {
          yield HandleAutofillValues({ autofillValues: itemObj.autofillValues, rowId: row.id, from: pItemId });
        }
      }
    }
    if (pDepartmentObj && pDepartment && pDepartmentObj.autofillValues?.length > 0) {
      yield HandleAutofillValues({ autofillValues: pDepartmentObj.autofillValues, rowId: row.id, from: pDepartment });
    }
    if (pLocationObj && pLocation && pLocationObj.autofillValues?.length > 0) {
      yield HandleAutofillValues({ autofillValues: pLocationObj.autofillValues, rowId: row.id, from: pLocation });
    }
  } catch (error) {
    reportError(error, {
      type: 'AUTOFILL_ERROR',
      payload: {
        row,
        store,
      },
    });
  }
}

export { GenerateRowAutofill, HandleAutofillValues };
